import type { ValidationIssues } from '~/models/common'
import type { OpinionRecord, RulesEngineOpinion } from '~/models/rulesOpinion'
import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import {
  applyWeeklyReviewDraftPatch,
  buildWeeklyReviewDraft,
  finalizeWeeklyReviewRecord,
  type PhaseSnapshot,
  type PlanSnapshot,
  type WeeklyReviewDraftInput,
  type WeeklyReviewDraftPatch,
  type WeeklyReviewRecord,
} from '~/models/weeklyReview'
import type { CalendarDate } from '~/types/dates'
import { toIsoTimestamp, unwrapDate } from '~/types/dates'
import { newId, type WeeklyReviewId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'
import { withTransaction } from '../transactions'

function invalidRecord(issues: ValidationIssues): StorageError {
  return storageError('invalidRecord', issues.map((entry) => entry.message).join(' '), issues)
}

export async function getWeeklyReview(
  db: SolaDatabase,
  id: WeeklyReviewId,
): Promise<Result<WeeklyReviewRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.weeklyReviews, id))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** Every review, oldest first — the `weekStartDate` index gives ascending order. */
export async function listWeeklyReviews(
  db: SolaDatabase,
): Promise<Result<readonly WeeklyReviewRecord[], StorageError>> {
  try {
    return ok(await db.getAllFromIndex(STORE.weeklyReviews, 'weekStartDate'))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function listWeeklyReviewsForWeek(
  db: SolaDatabase,
  weekStartDate: CalendarDate,
): Promise<Result<readonly WeeklyReviewRecord[], StorageError>> {
  try {
    return ok(
      await db.getAllFromIndex(STORE.weeklyReviews, 'weekStartDate', unwrapDate(weekStartDate)),
    )
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * The review in progress, if there is one.
 *
 * At most one draft is expected to exist. Where several somehow do — a restored
 * dataset, or a second tab, which `invalidation.ts` cannot see across — the most
 * recent week wins rather than an arbitrary one.
 */
export async function getDraftWeeklyReview(
  db: SolaDatabase,
): Promise<Result<WeeklyReviewRecord | undefined, StorageError>> {
  try {
    const drafts = await db.getAllFromIndex(STORE.weeklyReviews, 'status', 'draft')
    const sorted = [...drafts].sort((left, right) =>
      right.weekStartDate.localeCompare(left.weekStartDate),
    )
    return ok(sorted[0])
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function createWeeklyReviewDraft(
  db: SolaDatabase,
  input: WeeklyReviewDraftInput,
  now: Date,
): Promise<Result<WeeklyReviewRecord, StorageError>> {
  const built = buildWeeklyReviewDraft(input, toIsoTimestamp(now))
  if (!built.ok) return err(invalidRecord(built.error))

  try {
    await db.put(STORE.weeklyReviews, built.value)
    notifyWritten(STORE.weeklyReviews)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Fill in part of a draft.
 *
 * **A finalized review is refused, and there is exactly one guard doing it** —
 * `applyWeeklyReviewDraftPatch`, which this delegates to and whose refusal
 * arrives here as a typed `invalidRecord`. A second check in this function would
 * be unreachable while the first stands, and an unreachable guard is a liability
 * rather than defence in depth.
 *
 * What it protects: a finished week carries an opinion the user has already
 * read, and rewriting the evidence underneath it would make the app appear to
 * have said something it never said (D-046).
 *
 * Corrections to a finished week are not a missing feature to be added quietly —
 * they are unspecified, and would need their own decision in the shape of D-070.
 */
export async function updateWeeklyReviewDraft(
  db: SolaDatabase,
  id: WeeklyReviewId,
  patch: WeeklyReviewDraftPatch,
  now: Date,
): Promise<Result<WeeklyReviewRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.weeklyReviews, id)
    if (existing === undefined) {
      return err(storageError('invalidRecord', 'That weekly review no longer exists.'))
    }

    const patched = applyWeeklyReviewDraftPatch(existing, patch, toIsoTimestamp(now))
    if (!patched.ok) return err(invalidRecord(patched.error))

    await db.put(STORE.weeklyReviews, patched.value)
    notifyWritten(STORE.weeklyReviews)
    return ok(patched.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export interface FinalizeInput {
  readonly reviewId: WeeklyReviewId
  readonly phaseSnapshot: PhaseSnapshot
  /** Absent means "not recorded", never "an empty plan" (D-099 as amended). */
  readonly planSnapshot?: PlanSnapshot
  readonly opinion: RulesEngineOpinion
}

export interface FinalizeOutput {
  readonly review: WeeklyReviewRecord
  readonly opinion: OpinionRecord
}

/**
 * Finish a week: write the review, its immutable phase snapshot, and its opinion
 * **in one transaction**.
 *
 * The atomicity is the point. A review marked complete whose opinion failed to
 * write would show a finished week with no recommendation behind it, and an
 * opinion whose review never completed would be an orphan the export layer could
 * still find. Either is worse than the whole finalize failing and the user
 * tapping again, so a failure leaves zero records.
 *
 * Both records are built **before** the transaction opens. Nothing inside it
 * awaits anything but IndexedDB — awaiting any other promise would let the
 * transaction auto-commit early and write half of this (see transactions.ts).
 */
export async function finalizeWeeklyReview(
  db: SolaDatabase,
  input: FinalizeInput,
  now: Date,
): Promise<Result<FinalizeOutput, StorageError>> {
  const timestamp = toIsoTimestamp(now)

  const existing = await getWeeklyReview(db, input.reviewId)
  if (!existing.ok) return existing
  if (existing.value === undefined) {
    return err(storageError('invalidRecord', 'That weekly review no longer exists.'))
  }

  const opinionId = newId<'OpinionId'>()
  const opinionRecord: OpinionRecord = {
    id: opinionId,
    reviewId: input.reviewId,
    generatedAt: input.opinion.evaluatedAt,
    opinion: input.opinion,
    createdAt: timestamp,
    updatedAt: timestamp,
    schemaVersion: DB_SCHEMA_VERSION,
  }

  const finalized = finalizeWeeklyReviewRecord(
    existing.value,
    {
      phaseSnapshot: input.phaseSnapshot,
      ...(input.planSnapshot === undefined ? {} : { planSnapshot: input.planSnapshot }),
      opinionId,
    },
    timestamp,
  )
  if (!finalized.ok) return err(invalidRecord(finalized.error))

  const written = await withTransaction(
    db,
    [STORE.weeklyReviews, STORE.opinions] as const,
    async (tx) => {
      /* Both requests are issued before either is awaited, so the transaction
         sees them in one turn and cannot auto-commit between them.

         Each rejection is marked handled as it is issued. When one write fails
         the transaction aborts, which rejects the *other* request too — but
         `Promise.all` surfaces only the first, so the sibling's AbortError would
         otherwise escape as an unhandled rejection. That is the same hazard
         `withTransaction` already guards for `tx.done`, and it showed up here as
         soon as a test made a write fail mid-transaction. */
      const reviewWrite = tx.objectStore(STORE.weeklyReviews).put(finalized.value)
      void reviewWrite.catch(() => undefined)
      const opinionWrite = tx.objectStore(STORE.opinions).put(opinionRecord)
      void opinionWrite.catch(() => undefined)

      await Promise.all([reviewWrite, opinionWrite])
    },
  )
  if (!written.ok) return err(written.error)

  notifyWritten(STORE.weeklyReviews, STORE.opinions)
  return ok({ review: finalized.value, opinion: opinionRecord })
}
